import { supabase } from '../services/supabaseClient.js';
import { getVisitorId } from './visitorId.js';

// Read UTM params, preferring the captured session copy but falling back to the
// live URL. The fallback matters on a first landing: React runs child effects
// before the parent App effect that calls captureUtmParams(), so the very first
// track() of a session (e.g. landing_view) can fire before storage is written —
// without the URL fallback that first event would lose its attribution.
function getUtmParams() {
  const stored = new URLSearchParams(sessionStorage.getItem('utm_params') || '');
  const url = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const pick = (k) => stored.get(k) || url.get(k) || null;
  return { utm_source: pick('utm_source'), utm_medium: pick('utm_medium'), utm_campaign: pick('utm_campaign') };
}

// The ref tag (?ref=qr, ?ref=<affiliate>, …): captured copy first, live URL as
// the same first-load fallback as UTM above.
function getRef() {
  const url = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('ref') : null;
  return localStorage.getItem('ref') || url || null;
}

function captureUtmParams() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('utm_source')) {
    sessionStorage.setItem('utm_params', params.toString());
  }
  const ref = params.get('ref');
  if (ref) {
    localStorage.setItem('ref', ref);
  }
  // Stash the first EXTERNAL referrer host (e.g. facebook.com) once per browser.
  // The router does full reloads, so later same-origin loads won't overwrite it;
  // this preserves where the visitor originally came from for signup_source even
  // when there's no ?ref / utm (e.g. an organic Facebook link with fbclid only).
  try {
    if (!localStorage.getItem('src_referrer') && document.referrer) {
      const host = new URL(document.referrer).hostname.replace(/^www\./, '');
      if (host && host !== window.location.hostname) localStorage.setItem('src_referrer', host);
    }
  } catch { /* ignore */ }
}

// Best available signup-attribution token at signup time: explicit ref tag →
// utm_source → the original external referrer host → 'direct'. Pure reads of
// the values captureUtmParams stashed; safe to call from anywhere.
function getSignupSource() {
  try {
    const url = new URLSearchParams(window.location.search);
    const ref = localStorage.getItem('ref') || url.get('ref');
    if (ref) return ref;
    const utm = url.get('utm_source')
      || new URLSearchParams(sessionStorage.getItem('utm_params') || '').get('utm_source');
    if (utm) return utm;
    return localStorage.getItem('src_referrer') || 'direct';
  } catch {
    return null;
  }
}

export { captureUtmParams, getSignupSource };

export async function track(eventName, properties = {}) {
  if (localStorage.getItem('adaptos_internal') === 'true') return;
  // Only record PRODUCTION events — preview deploys (*.vercel.app), localhost,
  // and test pages on other hosts all hit the same Supabase otherwise.
  if (typeof window !== 'undefined' && !/(^|\.)adaptos\.app$/.test(window.location.hostname)) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    await supabase.from('events').insert({
      event_name:     eventName,
      learner_id:     session?.user?.id || null,
      visitor_id:     getVisitorId(),
      session_id:     session?.access_token?.slice(-8) || null,
      properties,
      affiliate_code: getRef(),
      ...getUtmParams(),
    });
  } catch {
    // fire-and-forget — never throw, never block
  }
}
